import React, { memo, useMemo } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { BringgInput, Checkbox, Col, Collapse, InputNumber, Row, RouteCostFormSection } from '@bringg/react-components';
import { FormElement } from '@bringg-frontend/form-element';
import { useHasFeatureFlag, useRouteCostTranslations } from '@bringg-frontend/hooks';
import { PrivilegeTypes } from '@bringg/types';

import { useStores } from 'bringg-web/recipes';
import { useHasAccess } from 'bringg-web/utils/privileges';

import styles from './user-type-form-inputs.module.scss';

const ADDITIONAL_COSTS_SECTION_KEY = 'additional-costs-section';
const ROUTE_COST_SECTION_KEY = 'route-cost-section';

interface SectionHeaderProps {
	title: string;
	description?: string;
}

interface NumberInputFieldProps {
	name: string;
	label?: string;
	required?: boolean;
	disabled?: boolean;
}

const NumberInputField = memo(({ name, label = '', required = false, disabled = false }: NumberInputFieldProps) => {
	const numberInputSettings = useMemo(
		() => ({
			min: 0,
			step: 0.01,
			precision: 2,
			style: { width: '100%' }
		}),
		[]
	);

	return (
		<FormElement name={name} label={label} required={required}>
			<InputNumber {...numberInputSettings} disabled={disabled} />
		</FormElement>
	);
});

const SectionHeader = memo(({ title, description }: SectionHeaderProps) => {
	return (
		<div className={styles.inputsGroupHeader}>
			<span>{title}</span>
			{description ? <span className={styles.inputsGroupHeaderDescription}>{description}</span> : null}
		</div>
	);
});

export const UserTypeFormInputs = observer(() => {
	const { t } = useTranslation();
	const {
		merchantConfigurationsStore: { merchantCountry }
	} = useStores();

	const routeCostCalculationEnabled = useHasFeatureFlag('enable_route_cost_calculation');
	const additionalCostsEnabled = useHasFeatureFlag('enable_additional_driver_cost_fields');

	const editRouteCostCalculationAllowed = useHasAccess(PrivilegeTypes.ALLOW_DISPATCHER_EDIT_ROUTE_COST_CALCULATION);
	// TODO: when @bringg/types issue with type missaligning will be fixed - user PrivilegeTypes.ALLOW_DISPATCHER_EDIT_ADDITIONAL_DRIVER_COST_FIELDS
	const editOptimizationCostCalculationAllowed = useHasAccess(
		'allow_dispatcher_edit_additional_driver_cost_fields' as PrivilegeTypes
	);
	// TODO: move to a hook since in used here and vehicle-profiles/vehicle-profile-form.tsx
	const merchantDistanceUnit = useMemo(() => {
		return t(merchantCountry === 'us' || merchantCountry === 'uk' ? 'ORDER.MILES' : t('ORDER.KM'));
	}, [merchantCountry, t]);

	const routeCostTranslations = useRouteCostTranslations(merchantDistanceUnit);

	const activeSectionKeys = useMemo(() => {
		const keys: string[] = [];
		if (additionalCostsEnabled) {
			keys.push(ADDITIONAL_COSTS_SECTION_KEY);
		}
		if (routeCostCalculationEnabled) {
			keys.push(ROUTE_COST_SECTION_KEY);
		}

		return keys;
	}, [additionalCostsEnabled, routeCostCalculationEnabled]);

	return (
		<div>
			<div>
				<FormElement required name="title" label={t('USER_TYPE.TITLE')}>
					<BringgInput placeholder={t('USER_TYPE.TITLE')} />
				</FormElement>
				<FormElement name="default_for_merchant" valuePropName="checked">
					<Checkbox>{t('USER_TYPE.DEFAULT_FOR_MERCHANT')}</Checkbox>
				</FormElement>
			</div>

			<SectionHeader title={t('USER_TYPE.LIMITS')} />
			<div className={styles.nestedSection}>
				<Row gutter={24}>
					<Col span={24}>
						<div className={styles.nestedLabel}>{t('USER_TYPE.TASKS_PER_SHIFT')}</div>
						<FormElement required name="maximum_tasks_per_shift" label={t('USER_TYPE.MAX_TASKS')}>
							<InputNumber min={0} style={{ width: '100%' }} />
						</FormElement>
					</Col>
				</Row>
				<Row gutter={24}>
					<Col span={24}>
						<div className={styles.nestedLabel}>{t('USER_TYPE.VEHICLE_WEIGHT')}</div>
						<NumberInputField name="max_total_weight" label={t('USER_TYPE.VEHICLE_MAX_KG')} />
					</Col>
				</Row>

				<Row gutter={24}>
					<Col span={24}>
						<div className={styles.nestedLabel}>{`${t('USER_TYPE.SPEED_FACTOR')}:`}</div>
						<NumberInputField name="speed_factor" />
					</Col>
				</Row>

				<Row gutter={24}>
					<Col span={24}>
						<div className={styles.nestedLabel}>{t('USER_TYPE.VEHICLE_VOLUME')}</div>
					</Col>
					<Col span={8}>
						<NumberInputField name="vehicle_height" label={t('USER_TYPE.VEHICLE_HEIGHT')} />
					</Col>
					<Col span={8}>
						<NumberInputField name="vehicle_width" label={t('USER_TYPE.VEHICLE_WIDTH')} />
					</Col>
					<Col span={8}>
						<NumberInputField name="vehicle_length" label={t('USER_TYPE.VEHICLE_LENGTH')} />
					</Col>
				</Row>
			</div>
			<SectionHeader title={t('USER_TYPE.COST_SECTION_TITLE')} />
			<Row gutter={24}>
				<Col span={8}>
					<NumberInputField name="cost_per_task" label={t('USER_TYPE.COST_PER_TASK')} />
				</Col>
				<Col span={8}>
					<NumberInputField name="cost_per_shift" label={t('USER_TYPE.COST_PER_SHIFT')} />
				</Col>
			</Row>
			{activeSectionKeys.length > 0 && (
				<Collapse expandIconPosition="end" defaultActiveKey={activeSectionKeys} removeHeaderPadding>
					{additionalCostsEnabled && (
						<Collapse.Panel
							key={ADDITIONAL_COSTS_SECTION_KEY}
							forceRender
							header={
								<div className={styles.panelHeader}>
									<SectionHeader
										title={t('USER_TYPE.ADDITIONAL_COSTS_SECTION_TITLE')}
										description={t('USER_TYPE.ADDITIONAL_COSTS_SECTION_DESCRIPTION')}
									/>
								</div>
							}
						>
							<Row gutter={24}>
								<Col span={12}>
									<NumberInputField
										disabled={!editOptimizationCostCalculationAllowed}
										name="cost_per_minute_driving"
										label={t('USER_TYPE.COST_PER_MINUTE_DRIVING')}
									/>
								</Col>
								<Col span={12}>
									<NumberInputField
										disabled={!editOptimizationCostCalculationAllowed}
										name="cost_per_km_driving"
										label={t('USER_TYPE.COST_PER_KM_DRIVING')}
									/>
								</Col>
								<Col span={12}>
									<NumberInputField
										disabled={!editOptimizationCostCalculationAllowed}
										name="cost_per_minute_waiting"
										label={t('USER_TYPE.COST_PER_MINUTE_WAITING')}
									/>
								</Col>
								<Col span={12}>
									<NumberInputField
										disabled={!editOptimizationCostCalculationAllowed}
										name="cost_per_minute_service"
										label={t('USER_TYPE.COST_PER_MINUTE_SERVICE')}
									/>
								</Col>
							</Row>
						</Collapse.Panel>
					)}
					{routeCostCalculationEnabled && (
						<Collapse.Panel
							key={ROUTE_COST_SECTION_KEY}
							forceRender
							header={
								<div className={styles.panelHeader}>
									<SectionHeader
										title={t('VEHICLE_PROFILES.FORM.ROUTE_COST')}
										description={t('VEHICLE_PROFILES.FORM.ROUTE_COST_DESCRIPTION')}
									/>
								</div>
							}
						>
							<RouteCostFormSection
								childFieldsFormKey="route_cost_calculation_configuration"
								translations={routeCostTranslations}
								disabled={!editRouteCostCalculationAllowed}
							/>
						</Collapse.Panel>
					)}
				</Collapse>
			)}
		</div>
	);
});
